import { get } from 'lodash-es';
import { forwardRef, type ForwardedRef } from 'react';
import type { Observable } from 'rxjs';
import type { MarketDataSnapshot, Security } from '../../../types';
import { HStack, Stack } from '../../Core';
import { FormControlSizes } from '../../Form/types';
import { Icon, ICON_SIZES, IconName } from '../../Icons';
import { PriceQuickOptions, type PriceInputMode, type PriceQuickOption } from '../../QuickOptions';
import { AmountInput, type AmountInputProps } from '../AmountInput';
import { QuickOptionsContainer } from '../styles';
import { DEFAULT_PRICE_OPTIONS } from './usePriceInput';

export interface PriceInputProps extends AmountInputProps {
  marketDataObs?: Observable<MarketDataSnapshot | undefined>;
  security?: Security;
  quickOptions?: PriceQuickOption[];
  priceInputMode?: PriceInputMode;
  isRange?: boolean;
  endPrice?: string;
  endPriceInvalid?: boolean;
  endPriceTouched?: boolean;
  onEndPriceChange?: (value: string | undefined) => void;
}

export const PriceInput = forwardRef(
  (
    {
      security,
      value,
      onChange,
      disabled,
      marketDataObs,
      size,
      quickOptions = DEFAULT_PRICE_OPTIONS,
      priceInputMode,
      isRange = false,
      endPrice,
      onEndPriceChange,
      endPriceInvalid,
      endPriceTouched,
      ...props
    }: PriceInputProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const id = props.id ?? 'price';
    const singleFieldPrice = (
      <>
        <AmountInput
          asset={security}
          {...props}
          value={value}
          onChange={onChange}
          disabled={disabled}
          size={size}
          ref={ref}
        />
        {security &&
          marketDataObs && ( // security passed means we want to show quickoptions
            <QuickOptionsContainer data-testid={`quick-options-${id}`}>
              <PriceQuickOptions
                options={quickOptions}
                value={value}
                key={security.Symbol}
                security={security}
                onChange={onChange}
                disabled={disabled}
                marketDataObs={marketDataObs}
                priceInputMode={priceInputMode}
                narrow={isRange}
                size={isRange ? FormControlSizes.Xxs : undefined}
              />
            </QuickOptionsContainer>
          )}
      </>
    );
    if (isRange && onEndPriceChange) {
      return (
        <HStack alignItems="start">
          <Stack>{singleFieldPrice}</Stack>
          {isRange && (
            <Icon
              icon={IconName.ArrowRight}
              variant="Subtle"
              size={ICON_SIZES.TINY}
              spaceAbove="spacing12"
              spaceBefore="spacing2"
              spaceAfter="spacing2"
            />
          )}
          <Stack>
            {isRange && (
              <AmountInput
                asset={security}
                {...props}
                autoFocus={false}
                data-testid={`${get(props, 'data-testid')}-end`}
                invalid={endPriceInvalid}
                touched={endPriceTouched}
                value={endPrice || ''}
                onChange={onEndPriceChange}
                disabled={disabled}
                size={size}
                ref={ref}
              />
            )}
            {isRange &&
              security &&
              marketDataObs && ( // security passed means we want to show quickoptions
                <QuickOptionsContainer data-testid={`quick-options-end-${id}`}>
                  <PriceQuickOptions
                    options={quickOptions}
                    value={endPrice}
                    key={security.Symbol}
                    security={security}
                    onChange={onEndPriceChange}
                    disabled={disabled}
                    marketDataObs={marketDataObs}
                    priceInputMode={priceInputMode}
                    size={FormControlSizes.Xxs}
                    narrow
                  />
                </QuickOptionsContainer>
              )}
          </Stack>
        </HStack>
      );
    }
    return singleFieldPrice;
  }
);
